import { useRef, type KeyboardEvent, type ReactNode } from 'react';

export interface TabDef {
  id: string;
  label: string;
  /** Optional lucide icon, sized h-4 w-4 by the caller. */
  icon?: ReactNode;
  /** Optional trailing adornment (e.g. a count Pill). */
  badge?: ReactNode;
}

/**
 * Controlled, router-free tab strip. URL or state sync is the caller's
 * job; this only renders the tablist and fires onSelect. Keyboard
 * support follows the WAI-ARIA tabs pattern with automatic activation:
 * arrows move focus and select, Home / End jump to the ends.
 */
export function Tabs({
  tabs,
  active,
  onSelect,
  label,
}: {
  tabs: TabDef[];
  active: string;
  onSelect: (id: string) => void;
  label: string;
}) {
  const listRef = useRef<HTMLDivElement>(null);

  function focusAndSelect(index: number) {
    const tab = tabs[(index + tabs.length) % tabs.length];
    if (!tab) return;
    onSelect(tab.id);
    const el = listRef.current?.querySelector<HTMLButtonElement>(
      `[data-tab-id="${tab.id}"]`,
    );
    el?.focus();
  }

  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        focusAndSelect(index + 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        focusAndSelect(index - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusAndSelect(0);
        break;
      case 'End':
        e.preventDefault();
        focusAndSelect(tabs.length - 1);
        break;
      default:
        break;
    }
  }

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={label}
      className="-mx-4 flex overflow-x-auto whitespace-nowrap border-b border-line/10 px-4 sm:mx-0 sm:px-0"
    >
      {tabs.map((t, i) => {
        const selected = t.id === active;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`tab-${t.id}`}
            data-tab-id={t.id}
            aria-selected={selected}
            aria-controls={`tabpanel-${t.id}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onSelect(t.id)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className={[
              'inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-strong focus-visible:ring-offset-1',
              selected
                ? 'border-accent text-fg'
                : 'border-transparent text-fg-muted hover:text-fg',
            ].join(' ')}
          >
            {t.icon}
            {t.label}
            {t.badge}
          </button>
        );
      })}
    </div>
  );
}

/** Renders its children only while its tab is the active one. */
export function TabPanel({
  tabId,
  active,
  children,
}: {
  tabId: string;
  active: string;
  children: ReactNode;
}) {
  if (tabId !== active) return null;
  return (
    <div
      role="tabpanel"
      id={`tabpanel-${tabId}`}
      aria-labelledby={`tab-${tabId}`}
      className="space-y-6"
    >
      {children}
    </div>
  );
}
